import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './MessageReactions.css';

export const REACTION_MAP = {
  like: { emoji: '👍', label: 'Like' },
  heart: { emoji: '❤️', label: 'Heart' },
  // laugh: { emoji: "😂", label: "Laugh" },
  // wow: { emoji: "😮", label: "Wow" },
  // sad: { emoji: "😢", label: "Sad" },
  // angry: { emoji: "😡", label: "Angry" },
};

function normalizeReactionType(v) {
  return String(v || '')
    .trim()
    .toLowerCase();
}

function isActiveReaction(reaction) {
  if (reaction?.is_deleted === undefined && reaction?.is_active === undefined) return true;
  const isDeleted = reaction?.is_deleted === true;
  const isActive = reaction?.is_active !== false;
  return !isDeleted && isActive;
}

function safeUuid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {}
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getOrCreateLocalReactorId() {
  try {
    const key = 'sure_chat_reactor_id';
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const created = safeUuid();
    localStorage.setItem(key, created);
    return created;
  } catch {
    return safeUuid();
  }
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

export default function MessageReactions({
  reactions = [],
  allowedReactions = [],
  currentUserNativeId = null,
  currentReactorType = 'user',
  side = 'left',
  onSetReaction,
  onDeleteReaction,
}) {
  const [local, setLocal] = useState(Array.isArray(reactions) ? reactions : []);
  const [pickerOpen, setPickerOpen] = useState(false);

  const [isHoveringBubble, setIsHoveringBubble] = useState(false);
  const [isHoveringBar, setIsHoveringBar] = useState(false);

  const [floating, setFloating] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);

  const rootRef = useRef(null);
  const bubbleRef = useRef(null);
  const floatingBarRef = useRef(null);
  const kebabBtnRef = useRef(null);
  const pickerRef = useRef(null); // <-- NEW: portal picker ref (so outside-click doesn't close on itself)

  const hideTimerRef = useRef(null);

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const scheduleHide = () => {
    clearHideTimer();
    hideTimerRef.current = setTimeout(() => {
      if (!isHoveringBubble && !isHoveringBar && !pickerOpen) {
        setFloating(false);
        setAnchorRect(null);
        setPickerOpen(false);
      }
    }, 160);
  };

  useEffect(() => () => clearHideTimer(), []);

  useEffect(() => {
    setLocal(Array.isArray(reactions) ? reactions : []);
  }, [reactions]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const stack = root.closest('.message-stack');
    if (!stack) return;

    const bubble = stack.querySelector('.message-bubble');
    bubbleRef.current = bubble || null;
  }, []);

  const myId = useMemo(() => {
    const v = currentUserNativeId ? String(currentUserNativeId) : '';
    return v || getOrCreateLocalReactorId();
  }, [currentUserNativeId]);

  const activeReactions = useMemo(() => {
    const arr = Array.isArray(local) ? local : [];
    return arr.filter(isActiveReaction);
  }, [local]);

  const allowedList = useMemo(() => {
    const base =
      Array.isArray(allowedReactions) && allowedReactions.length > 0
        ? allowedReactions
        : Object.keys(REACTION_MAP);

    const out = [];
    const seen = new Set();

    for (const v of base) {
      const t = normalizeReactionType(v);
      if (!REACTION_MAP[t]) continue;
      if (seen.has(t)) continue;
      seen.add(t);
      out.push(t);
    }
    return out;
  }, [allowedReactions]);

  const allowedSet = useMemo(() => new Set(allowedList), [allowedList]);

  const quickReactions = useMemo(() => allowedList.slice(0, 3), [allowedList]);

  const showKebab = allowedList.length > 3;

  const menuReactions = useMemo(() => allowedList, [allowedList]);

  const grouped = useMemo(() => {
    const byType = new Map();

    for (const reaction of activeReactions) {
      const t = normalizeReactionType(reaction?.reaction);
      if (!REACTION_MAP[t]) continue;

      const arr = byType.get(t) || [];
      arr.push(reaction);
      byType.set(t, arr);
    }

    return Array.from(byType.entries())
      .map(([type, items]) => {
        const mine = items.some((x) => String(x?.reactor_native_id) === myId);
        return { type, count: items.length, mine };
      })
      .filter((g) => allowedSet.has(g.type));
  }, [activeReactions, myId, allowedSet]);

  const recomputeFloating = () => {
    const el = bubbleRef.current;
    if (!el) return;

    const r = el.getBoundingClientRect();
    const shouldFloat = r.top < 8 && r.bottom > 60;

    setFloating(shouldFloat);
    setAnchorRect(shouldFloat ? r : null);
  };

  useEffect(() => {
    const el = bubbleRef.current;
    if (!el) return;

    const onEnter = () => {
      clearHideTimer();
      setIsHoveringBubble(true);
      recomputeFloating();
      window.addEventListener('scroll', recomputeFloating, true);
      window.addEventListener('resize', recomputeFloating);
    };

    const onMove = () => recomputeFloating();

    const onLeave = () => {
      setIsHoveringBubble(false);
      scheduleHide();

      window.removeEventListener('scroll', recomputeFloating, true);
      window.removeEventListener('resize', recomputeFloating);
    };

    el.addEventListener('pointerenter', onEnter);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);

    return () => {
      el.removeEventListener('pointerenter', onEnter);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
      window.removeEventListener('scroll', recomputeFloating, true);
      window.removeEventListener('resize', recomputeFloating);
    };
  }, [pickerOpen, isHoveringBar, isHoveringBubble]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!pickerOpen) return;

      const root = rootRef.current;
      const fb = floatingBarRef.current;
      const pickerEl = pickerRef.current;

      const insideRoot = root && root.contains(e.target);
      const insideFloating = fb && fb.contains(e.target);
      const insidePicker = pickerEl && pickerEl.contains(e.target); // <-- NEW

      if (!insideRoot && !insideFloating && !insidePicker) {
        setPickerOpen(false);
        scheduleHide();
      }
    };

    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [pickerOpen, isHoveringBar, isHoveringBubble]);

  const toggleReaction = async (type) => {
    const reactionType = normalizeReactionType(type);
    if (!REACTION_MAP[reactionType]) return;
    if (!allowedSet.has(reactionType)) return;

    const hadMine = activeReactions.some(
      (reaction) =>
        normalizeReactionType(reaction?.reaction) === reactionType &&
        String(reaction?.reactor_native_id) === myId,
    );

    const prev = local;

    try {
      if (hadMine) {
        setLocal(
          prev.map((reaction) => {
            if (
              isActiveReaction(reaction) &&
              normalizeReactionType(reaction?.reaction) === reactionType &&
              String(reaction?.reactor_native_id) === myId
            ) {
              return {
                ...reaction,
                is_active: false,
                is_deleted: true,
                updated_at: new Date().toISOString(),
              };
            }
            return reaction;
          }),
        );

        if (typeof onDeleteReaction === 'function') {
          await onDeleteReaction(reactionType);
        }
        setPickerOpen(false);
        return;
      }

      setLocal([
        ...prev,
        {
          reaction: reactionType,
          reaction_type: currentReactorType,
          reactor_native_id: myId,
          updated_at: new Date().toISOString(),
          is_active: true,
          is_deleted: false,
        },
      ]);

      if (typeof onSetReaction === 'function') {
        await onSetReaction(reactionType);
      }
      setPickerOpen(false);
    } catch (e) {
      setLocal(prev);
      console.error('Reaction API failed:', e);
    }
  };

  const showHoverBar = isHoveringBubble || isHoveringBar || pickerOpen;
  const alignClass = side === 'right' ? 'right' : 'left';

  const floatingStyle = useMemo(() => {
    if (!floating || !anchorRect) return null;

    const barWidth = 300;
    const left = clamp(
      anchorRect.left + anchorRect.width / 2 - barWidth / 2,
      2,
      window.innerWidth - barWidth - 8,
    );

    return { left: `${left}px`, top: `60px` };
  }, [floating, anchorRect]);

  const onBarEnter = () => {
    clearHideTimer();
    setIsHoveringBar(true);
  };
  const onBarLeave = () => {
    setIsHoveringBar(false);
    scheduleHide();
  };

  const renderHoverBarInner = () => (
    <>
      {quickReactions.map((t) => (
        <button
          key={t}
          type="button"
          className="reaction-quick"
          onClick={() => toggleReaction(t)}
          aria-label={REACTION_MAP[t]?.label}
        >
          <span className="reaction-emoji">{REACTION_MAP[t]?.emoji}</span>
        </button>
      ))}

      {showKebab && (
        <button
          ref={kebabBtnRef}
          type="button"
          className={`reaction-kebab-btn ${pickerOpen ? 'open' : ''}`}
          onClick={() => {
            clearHideTimer();
            setPickerOpen((v) => !v);
          }}
          aria-label="More reactions"
        >
          ⋮
        </button>
      )}
    </>
  );

  const renderPicker = () => {
    if (!pickerOpen) return null;

    const btn = kebabBtnRef.current;
    const rect = btn?.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth;

    const width = 100;

    const xRaw = rect ? rect.right - width : 8;

    const x = clamp(xRaw, 8, vw - width - 8);
    const y = rect ? rect.bottom + 8 : 48;

    return createPortal(
      <div
        ref={pickerRef}
        className="reaction-picker"
        style={{ left: `${x}px`, top: `${y}px` }}
        onPointerEnter={onBarEnter}
        onPointerLeave={onBarLeave}
      >
        {menuReactions.map((t) => (
          <button
            key={t}
            type="button"
            className="reaction-picker-item"
            onClick={() => toggleReaction(t)}
            aria-label={REACTION_MAP[t]?.label}
          >
            <span className="reaction-picker-emoji">{REACTION_MAP[t]?.emoji}</span>
          </button>
        ))}
      </div>,
      document.body,
    );
  };

  return (
    <div ref={rootRef} className="message-reactions-root">
      {showHoverBar && !floating && (
        <div
          className={`reaction-hoverbar ${alignClass} ${pickerOpen ? 'keep-open' : ''}`} // <-- NEW
          onPointerEnter={onBarEnter}
          onPointerLeave={onBarLeave}
        >
          {renderHoverBarInner()}
        </div>
      )}

      {showHoverBar && floating && (
        <div
          ref={floatingBarRef}
          className={`reaction-hoverbar reaction-hoverbar-floating ${alignClass} ${pickerOpen ? 'keep-open' : ''}`} // <-- NEW
          style={floatingStyle || undefined}
          onPointerEnter={onBarEnter}
          onPointerLeave={onBarLeave}
        >
          {renderHoverBarInner()}
        </div>
      )}

      {grouped.length > 0 && (
        <div className={`reaction-chips ${alignClass}`}>
          {grouped.map((g) => (
            <button
              key={g.type}
              type="button"
              className={`reaction-chip ${g.mine ? 'mine' : ''}`}
              onClick={() => toggleReaction(g.type)}
              aria-label={`${REACTION_MAP[g.type]?.label} ${g.count}`}
            >
              <span className="reaction-chip-emoji">{REACTION_MAP[g.type]?.emoji}</span>
              <span className="reaction-chip-count">{g.count}</span>
            </button>
          ))}
        </div>
      )}

      {renderPicker()}
    </div>
  );
}
